import {
  CmsContentHomeContentSchema,
  HOME_CONTENT_DISPLAY_MODE_OPTIONS,
  LAYOUT_PRESET_OPTIONS,
  type CmsContentHomeContentDisplayMode,
  type CmsContentHomeContentForm,
  type CmsContentHomeContentLayoutPreset,
} from "@/modules/cms/domain/contracts";
import { ProductCheckboxPicker } from "@/modules/products/presentation/ui/ProductCheckboxPicker";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import {
  Button,
  FormCheckbox,
  FormInput,
  FormRoot,
  RHF,
  cn,
  zodResolver,
} from "@devhop/ui";
import { useCallback, useEffect, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";

const PRESET_SLOT_KEYS = ["a", "b", "c", "d"] as const;
const HOME_CONTENT_HERO_IMAGE_KEY_PREFIX = "uploads/cms/home-content-hero";

const HERO_IMAGE_FIELDS = [
  "heroMobileImageKey",
  "heroTabletImageKey",
  "heroDesktopImageKey",
] as const;
type HeroImageField = (typeof HERO_IMAGE_FIELDS)[number];
type PendingHeroSlot = "mobile" | "tablet" | "desktop";

const HERO_IMAGE_UPLOADS: {
  slot: PendingHeroSlot;
  field: HeroImageField;
  label: string;
  aspectHint: string;
  aspectRatio: string;
  widthPx: number;
  heightPx: number;
}[] = [
  {
    slot: "mobile",
    field: "heroMobileImageKey",
    label: "Mobile (4:5)",
    aspectHint: "ອັດສ່ວນ 4:5",
    aspectRatio: "aspect-[4/5]",
    widthPx: 800,
    heightPx: 1000,
  },
  {
    slot: "tablet",
    field: "heroTabletImageKey",
    label: "Tablet (16:9)",
    aspectHint: "ອັດສ່ວນ 16:9",
    aspectRatio: "aspect-video",
    widthPx: 1280,
    heightPx: 720,
  },
  {
    slot: "desktop",
    field: "heroDesktopImageKey",
    label: "Desktop (8:3)",
    aspectHint: "ອັດສ່ວນ 8:3",
    aspectRatio: "aspect-8/3",
    widthPx: 1920,
    heightPx: 720,
  },
];

function readStringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/** ภาพตัวอย่าง layout: จำนวนกล่องตาม preset (1–4) */
function LayoutPresetPreview({
  count,
}: {
  count: CmsContentHomeContentLayoutPreset;
}) {
  return (
    <div className="flex h-9 w-full gap-0.5 rounded border border-border bg-muted/30 p-1">
      {PRESET_SLOT_KEYS.slice(0, count).map((slotId) => (
        <div
          key={slotId}
          className="flex-1 rounded-sm bg-muted-foreground/20"
          aria-hidden
        />
      ))}
    </div>
  );
}

/** ตัวอย่าง hero: แถบ artwork ใหญ่ + แถบสินค้าเล็ก */
function HeroDisplayPreview() {
  return (
    <div className="flex h-9 w-full flex-col gap-0.5 rounded border border-border bg-muted/30 p-1">
      <div className="min-h-0 flex-2 rounded-sm bg-muted-foreground/25" aria-hidden />
      <div className="h-2 rounded-sm bg-muted-foreground/15" aria-hidden />
    </div>
  );
}

const layoutPresetSchema = z.coerce
  .number()
  .int()
  .pipe(z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]));

const productItemSchema = z.object({ value: z.string() });

const FormSchema = z
  .object({
    titleLo: z.string().optional(),
    titleTh: z.string().optional(),
    titleEn: z.string().optional(),
    displayMode: z.enum(["grid", "hero"]),
    layoutPreset: layoutPresetSchema.default(1),
    products: z.array(productItemSchema),
    heroMobileImageKey: z.string().default(""),
    heroTabletImageKey: z.string().default(""),
    heroDesktopImageKey: z.string().default(""),
    isActive: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (
      ![v.titleLo, v.titleTh, v.titleEn].some(
        (s) => typeof s === "string" && s.trim().length > 0,
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງໃສ່ຫົວຂໍ້ຢ່າງໜ້ອຍຫນຶ່ງພາສາ",
        path: ["titleLo"],
      });
    }
  });
type FormValues = z.infer<typeof FormSchema>;

type HomeContentFormProps = {
  content: CmsContentHomeContentForm | null;
  onSubmit: (content: CmsContentHomeContentForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

function toFormValues(c: CmsContentHomeContentForm | null): FormValues {
  if (!c) {
    return {
      titleLo: "",
      titleTh: "",
      titleEn: "",
      displayMode: "grid",
      layoutPreset: 1,
      products: [],
      heroMobileImageKey: "",
      heroTabletImageKey: "",
      heroDesktopImageKey: "",
      isActive: true,
    };
  }
  const t = c.title ?? {};
  const productIds = Array.isArray(c.products) ? c.products : [];
  const displayMode = c.displayMode === "hero" ? "hero" : "grid";
  return {
    titleLo: (t as { lo?: string }).lo ?? "",
    titleTh: (t as { th?: string }).th ?? "",
    titleEn: (t as { en?: string }).en ?? "",
    displayMode,
    layoutPreset: (displayMode === "hero" ? 1 : (c.layoutPreset ?? 1)) as 1 | 2 | 3 | 4,
    products: (displayMode === "hero" ? productIds.slice(0, 1) : productIds).map((value) => ({
      value,
    })),
    heroMobileImageKey:
      readStringField(c.heroMobileImageKey) || readStringField(c.heroArtworkKey),
    heroTabletImageKey:
      readStringField(c.heroTabletImageKey) || readStringField(c.heroArtworkKey),
    heroDesktopImageKey:
      readStringField(c.heroDesktopImageKey) || readStringField(c.heroArtworkKey),
    isActive: c.isActive ?? true,
  };
}

function toContent(v: FormValues): CmsContentHomeContentForm {
  const title: { lo?: string; th?: string; en?: string } = {};
  if (v.titleLo?.trim()) title.lo = v.titleLo.trim();
  if (v.titleTh?.trim()) title.th = v.titleTh.trim();
  if (v.titleEn?.trim()) title.en = v.titleEn.trim();
  const products = (v.products ?? [])
    .map((p) => (typeof p === "string" ? p : p.value).trim())
    .filter(Boolean);
  const displayMode = v.displayMode;
  const base: CmsContentHomeContentForm = {
    blockType: "product",
    title,
    displayMode,
    layoutPreset: displayMode === "hero" ? 1 : v.layoutPreset,
    products: displayMode === "hero" ? products.slice(0, 1) : products,
    isActive: v.isActive ?? true,
  };
  if (displayMode === "hero") {
    const mobile = v.heroMobileImageKey?.trim();
    const tablet = v.heroTabletImageKey?.trim();
    const desktop = v.heroDesktopImageKey?.trim();
    if (mobile) base.heroMobileImageKey = mobile;
    if (tablet) base.heroTabletImageKey = tablet;
    if (desktop) base.heroDesktopImageKey = desktop;
  }
  return base;
}

export function HomeContentForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: HomeContentFormProps) {
  const defaultValues = toFormValues(content);

  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(FormSchema) as Resolver<FormValues>,
    defaultValues,
  });

  const displayMode = methods.watch("displayMode");
  const [pendingHeroFiles, setPendingHeroFiles] = useState<Partial<Record<PendingHeroSlot, File>>>({});
  const [uploading, setUploading] = useState(false);

  // โหมด Hero ไม่แสดง layout preset — คงค่า 1 ไว้ใน form state ตลอด
  useEffect(() => {
    if (displayMode === "hero") {
      methods.setValue("layoutPreset", 1, { shouldValidate: false });
    }
  }, [displayMode, methods]);

  const productsError = methods.formState.errors.products?.message;

  const setPendingHeroFile = useCallback((slot: PendingHeroSlot, file: File | null) => {
    setPendingHeroFiles((prev) => {
      const next = { ...prev };
      if (file) next[slot] = file;
      else delete next[slot];
      return next;
    });
  }, []);

  const handleDisplayModeChange = useCallback(
    (mode: CmsContentHomeContentDisplayMode) => {
      methods.clearErrors([...HERO_IMAGE_FIELDS, "products"]);
      methods.setValue("displayMode", mode, { shouldDirty: true });
      if (mode === "hero") {
        methods.setValue("layoutPreset", 1, { shouldDirty: true });
        const current = methods.getValues("products") ?? [];
        if (current.length > 1) {
          methods.setValue("products", current.slice(0, 1), { shouldDirty: true });
        }
        for (const field of HERO_IMAGE_FIELDS) {
          const value = methods.getValues(field);
          if (typeof value !== "string") {
            methods.setValue(field, "", { shouldDirty: true });
          }
        }
      }
    },
    [methods],
  );

  const handleSubmit = async (vals: FormValues) => {
    methods.clearErrors([...HERO_IMAGE_FIELDS, "products"]);

    const heroKeys: Record<HeroImageField, string> = {
      heroMobileImageKey: readStringField(vals.heroMobileImageKey).trim(),
      heroTabletImageKey: readStringField(vals.heroTabletImageKey).trim(),
      heroDesktopImageKey: readStringField(vals.heroDesktopImageKey).trim(),
    };

    if (vals.displayMode === "hero" && Object.keys(pendingHeroFiles).length > 0) {
      setUploading(true);
      try {
        for (const upload of HERO_IMAGE_UPLOADS) {
          const file = pendingHeroFiles[upload.slot];
          if (!file) continue;
          const key = await uploadImageToKey(HOME_CONTENT_HERO_IMAGE_KEY_PREFIX, file);
          heroKeys[upload.field] = key;
          methods.setValue(upload.field, key, { shouldDirty: true });
        }
        setPendingHeroFiles({});
      } catch {
        methods.setError("heroMobileImageKey", {
          type: "manual",
          message: "ອັບໂຫຼດຮູບລົ້ມເຫຼວ",
        });
        return;
      } finally {
        setUploading(false);
      }
    }

    if (vals.displayMode === "hero") {
      let hasHeroImageError = false;
      for (const upload of HERO_IMAGE_UPLOADS) {
        if (!heroKeys[upload.field]) {
          methods.setError(upload.field, {
            type: "manual",
            message: `ຕ້ອງອັບໂຫຼດຮູບ ${upload.label}`,
          });
          hasHeroImageError = true;
        }
      }
      if (hasHeroImageError) return;

      const productCount = (vals.products ?? []).filter((p) => p.value?.trim()).length;
      if (productCount !== 1) {
        methods.setError("products", {
          type: "manual",
          message: "ໂໝດ Hero ຕ້ອງເລືອກສິນຄ້າ 1 ຊິ້ນ",
        });
        return;
      }
    }

    const data = toContent({
      ...vals,
      heroMobileImageKey: heroKeys.heroMobileImageKey,
      heroTabletImageKey: heroKeys.heroTabletImageKey,
      heroDesktopImageKey: heroKeys.heroDesktopImageKey,
    });
    CmsContentHomeContentSchema.parse(data);
    onSubmit(data);
  };

  return (
    <FormRoot<FormValues>
      methods={methods as UseFormReturn<FormValues>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <h3 className="font-medium">ບລ໋ອກສິນຄ້າ</h3>
        <p className="text-muted-foreground text-sm">
          ເລືອກແບບ Grid (1–4 ສິນຄ້າ) ຫຼື Hero (1 ສິນຄ້າ + artwork ເຕັມພື້ນທີ່)
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormInput name="titleLo" label="ຫົວຂໍ້ (ລາວ)" placeholder="ສິນຄ້າແນະນຳ" />
          <FormInput name="titleTh" label="ຫົວຂໍ້ (ໄທ)" placeholder="สินค้าแนะนำ" />
          <FormInput
            name="titleEn"
            label="ຫົວຂໍ້ (EN)"
            placeholder="Recommended"
          />
        </div>
        <RHF.Controller
          name="displayMode"
          control={methods.control}
          shouldUnregister={false}
          render={({ field }) => (
            <fieldset className="space-y-3">
              <legend className="font-medium text-sm">ຮູບແບບການສະແດງ</legend>
              <div className="grid grid-cols-2 gap-3">
                {HOME_CONTENT_DISPLAY_MODE_OPTIONS.map((opt) => {
                  const isSelected = field.value === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleDisplayModeChange(opt.value)}
                      className={cn(
                        "flex flex-col items-center gap-2 rounded-lg border-2 p-3 text-left transition-colors",
                        "hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-transparent bg-muted/20",
                      )}
                      aria-pressed={isSelected}
                    >
                      {opt.value === "hero" ? (
                        <HeroDisplayPreview />
                      ) : (
                        <LayoutPresetPreview count={3} />
                      )}
                      <span className="font-medium text-sm">{opt.label}</span>
                      {opt.description && (
                        <span className="text-muted-foreground text-xs">
                          {opt.description}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}
        />
        {displayMode === "grid" ? (
          <RHF.Controller
            name="layoutPreset"
            control={methods.control}
            shouldUnregister={false}
            defaultValue={defaultValues.layoutPreset}
            render={({ field }) => (
              <fieldset className="space-y-3">
                <legend className="font-medium text-sm">
                  ຈຳນວນສິນຄ້າທີ່ສະແດງ (layout preset)
                </legend>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {LAYOUT_PRESET_OPTIONS.map((opt) => {
                    const isSelected = Number(field.value) === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => field.onChange(opt.value)}
                        className={cn(
                          "flex flex-col items-center gap-2 rounded-lg border-2 p-3 text-left transition-colors",
                          "hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          isSelected
                            ? "border-primary bg-primary/5"
                            : "border-transparent bg-muted/20",
                        )}
                        aria-pressed={isSelected}
                      >
                        <LayoutPresetPreview count={opt.value} />
                        <span className="font-medium text-sm">{opt.label}</span>
                        {opt.description && (
                          <span className="text-muted-foreground text-xs">
                            {opt.description}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}
          />
        ) : (
          <fieldset className="space-y-3">
            <legend className="font-medium text-sm">Artwork (Hero)</legend>
            <p className="text-muted-foreground text-xs">
              ອັບໂຫຼດຮູບ 3 ຂະໜາດເຫມືອນ Hero ຫຼັກ — Mobile 4:5, Tablet 16:9, Desktop 8:3
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {HERO_IMAGE_UPLOADS.map((upload) => {
                const fieldError = methods.formState.errors[upload.field]?.message;
                const value = readStringField(methods.watch(upload.field));
                return (
                  <div key={upload.field} className="space-y-1">
                    <ImageKeyUploadField
                      keyPrefix={HOME_CONTENT_HERO_IMAGE_KEY_PREFIX}
                      label={upload.label}
                      aspectHint={upload.aspectHint}
                      aspectRatio={upload.aspectRatio}
                      widthPx={upload.widthPx}
                      heightPx={upload.heightPx}
                      value={value}
                      onChange={(key) => {
                        methods.clearErrors(upload.field);
                        methods.setValue(upload.field, key, { shouldDirty: true });
                      }}
                      disabled={submitting || uploading}
                      deferUpload
                      pendingFile={pendingHeroFiles[upload.slot] ?? null}
                      onFileSelect={(file) => {
                        methods.clearErrors(upload.field);
                        setPendingHeroFile(upload.slot, file);
                      }}
                      onClear={() => {
                        methods.setValue(upload.field, "", { shouldDirty: true });
                        setPendingHeroFile(upload.slot, null);
                        methods.clearErrors(upload.field);
                      }}
                    />
                    {fieldError ? (
                      <p className="text-destructive text-sm">{fieldError}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </fieldset>
        )}
        <RHF.Controller
          name="products"
          control={methods.control}
          shouldUnregister={false}
          render={({ field }) => (
            <fieldset className="space-y-2">
              <legend className="font-medium text-sm">
                {displayMode === "hero"
                  ? "ເລືອກສິນຄ້າ (1 ຊິ້ນ)"
                  : "ເລືອກສິນຄ້າ (ລຳດັບການສະແດງ = ລຳດັບທີ່ເລືອກ)"}
              </legend>
              <ProductCheckboxPicker
                value={(field.value ?? []).map((p) =>
                  typeof p === "string" ? p : p.value,
                )}
                onChange={(ids) => {
                  methods.clearErrors("products");
                  const next = displayMode === "hero" ? ids.slice(0, 1) : ids;
                  field.onChange(next.map((value) => ({ value })));
                }}
                searchPlaceholder="ຄົ້ນຫາຕາມຊື່ສິນຄ້າ..."
                listHeight={280}
              />
              {productsError ? (
                <p className="text-destructive text-sm">{productsError}</p>
              ) : null}
            </fieldset>
          )}
        />
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ (ສະແດງບລ໋ອກນີ້ໃນຫນ້າຫຼັກ)" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting || uploading}>
            {uploading
              ? "ກຳລັງອັບໂຫຼດ..."
              : submitting
                ? "ກຳລັງບັນທຶກ..."
                : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
